import { Button } from '@/components/common/button'
import { Note } from '@/components/common/note'
import { TabsContent } from '@/components/common/tabs'
import { useConnectWalletModalContext } from '@/components/connect/connectModal'
import { FundWalletOnSupply } from '@/components/opportunity/fundWalletOnSupply'
import type { TabConfig } from '@/components/opportunity/useTabsConfig'
import { useCrosschainDeposit } from '@/hooks/useCrosschainDeposit'
import { useCrosschainSuccessHandler } from '@/hooks/useCrosschainSuccessHandler'
import { useCrosschainQuoteQuery } from '@/queries/crosschainQuote.query'
import { useCrosschainTransactionStatusQuery } from '@/queries/crosschainTransactionStatus.query'
import type { TransactionsContext } from '@/queries/transactionsContext.query'
import type { OpportunityLoader } from '@/routes/opportunity.$network.$vaultId'
import type { CrosschainOption } from '@/types/crosschainOption'
import type { StepData } from '@/types/transactionsContext'
import { useCallback, useEffect, useState } from 'react'
import { useLoaderData } from 'react-router'
import type { Address } from 'viem'
import { formatUnits } from 'viem'
import { CrosschainAssetSelect } from '../sidePanel/crosschainAssetSelect'
import { CrosschainSignPermit } from '../sidePanel/crosschainDepositButton'
import { CrosschainFailedState } from '../sidePanel/crosschainFailedState'
import { CrosschainPendingState } from '../sidePanel/crosschainPendingState'
import { CrosschainQuoteBreakdown } from '../sidePanel/crosschainQuoteBreakdown'
import { DepositLimitWarning } from '../sidePanel/depositLimitWarning'
import { useDepositLimit } from '../sidePanel/useDepositLimit'
import { ActionNotes } from './actionNotes'
import { AmountInputSection } from './amountInputSection'

interface CrosschainActionTabProps {
  account: Address | undefined
  stepData: StepData
  tabConfig: TabConfig
  transactionsContext: TransactionsContext
  setTransactionFinished: ({ txHash }: { txHash: string | undefined; amount: string }) => void
  txState: 'idle' | 'loading' | 'success'
  crosschainOptions: CrosschainOption[]
  selectedInput: CrosschainOption
  setSelectedInput: (input: CrosschainOption) => void
}

export const CrosschainActionTab = ({
  account,
  stepData,
  tabConfig,
  transactionsContext,
  setTransactionFinished,
  txState,
  crosschainOptions,
  selectedInput,
  setSelectedInput,
}: CrosschainActionTabProps) => {
  const { setOpenModal: setOpenWalletModal } = useConnectWalletModalContext()
  const { vault } = useLoaderData<OpportunityLoader>()
  const { getParsedAmount, actionDescription, form } = tabConfig
  const { network, asset, lpToken } = vault
  const inputAmount = form.watch('amount')
  const parsedAmount = getParsedAmount(inputAmount || '0')

  const [shouldFetchQuote, setShouldFetchQuote] = useState(false)
  const [displayFundWallet, setDisplayFundWallet] = useState(false)

  const canGetQuote = parsedAmount !== '0'

  useEffect(() => {
    if (!canGetQuote) setShouldFetchQuote(false)
  }, [canGetQuote])

  const {
    data: crosschainQuote,
    isFetching: isCrosschainQuoteFetching,
    error: crosschainQuoteError,
    refetch: refetchQuote,
  } = useCrosschainQuoteQuery({
    userAddress: account,
    inputToken: { address: selectedInput.address, network: selectedInput.network },
    outputVault: {
      address: vault.address as Address,
      network: vault.network.name,
    },
    amount: parsedAmount,
    enabled: shouldFetchQuote && !!account && parsedAmount !== '0',
  })
  const handleQuoteExpired = useCallback(() => {
    if (shouldFetchQuote && !isCrosschainQuoteFetching) refetchQuote()
  }, [shouldFetchQuote, isCrosschainQuoteFetching, refetchQuote])

  const isExceedingBalance = parsedAmount !== '0' && BigInt(parsedAmount) > selectedInput.balanceNative

  const crosschainErrorMessage =
    crosschainQuoteError?.message || (crosschainQuoteError ? 'Failed to fetch crosschain quote' : null)

  const {
    executeDeposit: executeCrosschainDeposit,
    isPending: isCrosschainPending,
    error: crosschainDepositError,
    crosschainTransactionId,
    clearTransaction,
    currentStep: crosschainCurrentStep,
    totalSteps: crosschainTotalSteps,
  } = useCrosschainDeposit(crosschainQuote)

  const { data: crosschainTransactionStatus } = useCrosschainTransactionStatusQuery({
    crosschainTransactionId,
    enabled: !!crosschainTransactionId,
  })

  useCrosschainSuccessHandler({
    crosschainTransactionStatus,
    crosschainTransactionId,
    inputAmount,
    setTransactionFinished,
  })

  const onSubmit = form.handleSubmit(async () => {
    if (!account) {
      setOpenWalletModal('connect')
      return
    }
    await executeCrosschainDeposit()
  })

  const { depositLimit, depositLimitValue, isAboveDepositLimit } = useDepositLimit({
    transactionsContext,
    parsedAmount,
    assetDecimals: asset.decimals,
    isDepositAction: stepData.name === 'deposit',
  })

  const isNonIdle = txState !== 'idle'

  return (
    <form onSubmit={onSubmit}>
      <FundWalletOnSupply
        depositConfig={tabConfig}
        open={displayFundWallet}
        network={selectedInput.network}
        asset={{ symbol: selectedInput.symbol, decimals: selectedInput.decimals }}
        walletBalance={selectedInput.balanceNative.toString()}
        closeModal={() => setDisplayFundWallet(false)}
      />

      <TabsContent value={stepData.name} className="mt-3 flex flex-col gap-3 md:mt-4 md:gap-4">
        <AmountInputSection
          asset={asset}
          form={form}
          inputAmount={inputAmount}
          availableBalance={selectedInput.balanceNative.toString()}
          isNonIdle={isNonIdle}
          actionDescription={actionDescription}
          tokenSelector={
            <CrosschainAssetSelect
              asset={asset}
              network={network}
              selectedInput={selectedInput}
              crosschainOptions={crosschainOptions}
              setSelectedInput={setSelectedInput}
              className="w-26"
            />
          }
        />

        {!shouldFetchQuote && (
          <Button
            type="button"
            className="h-13 gap-2"
            disabled={!canGetQuote}
            onClick={() => {
              if (isExceedingBalance) {
                setDisplayFundWallet(true)
              } else {
                setShouldFetchQuote(true)
              }
            }}
          >
            Get cross-chain quote
          </Button>
        )}

        {crosschainErrorMessage && shouldFetchQuote && (
          <Note color="red" className="break-words">
            <p className="inline-block first-letter:uppercase">{crosschainErrorMessage}</p>
          </Note>
        )}

        {shouldFetchQuote && (
          <CrosschainQuoteBreakdown
            inputAmount={parsedAmount}
            inputDecimals={selectedInput.decimals}
            inputSymbol={selectedInput.symbol}
            isFetching={isCrosschainQuoteFetching}
            quote={crosschainQuote}
            receivedTokenSymbol={lpToken?.symbol ?? asset.symbol}
            underlyingSymbol={asset.symbol}
            onQuoteExpired={handleQuoteExpired}
          />
        )}

        {isAboveDepositLimit && depositLimitValue && depositLimit !== undefined && (
          <DepositLimitWarning
            depositLimitValue={depositLimitValue}
            assetSymbol={asset.symbol}
            onClick={() => {
              form.setValue('amount', formatUnits(depositLimit, asset.decimals), { shouldValidate: true })
            }}
          />
        )}

        {crosschainTransactionId ? (
          crosschainTransactionStatus?.status === 'failed' ? (
            <CrosschainFailedState error={new Error('Transaction failed on chain')} onRetry={clearTransaction} />
          ) : (
            <CrosschainPendingState />
          )
        ) : shouldFetchQuote ? (
          <CrosschainSignPermit
            disabled={!crosschainQuote || parsedAmount === '0'}
            loading={isCrosschainPending}
            error={crosschainDepositError}
            onClick={onSubmit}
            currentStep={crosschainCurrentStep}
            totalSteps={crosschainTotalSteps}
          />
        ) : null}

        <ActionNotes
          vault={vault}
          stepData={stepData}
          transactionsContext={transactionsContext}
          inputAmount={inputAmount}
        />
      </TabsContent>
    </form>
  )
}
